import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  TrendingUp,
  Calendar,
  Search,
  MoreVertical,
  Sparkles,
  Target,
  Award,
  FileText,
  PhoneCall,
  X,
  Phone,
} from "lucide-react";
import {
  RiFileTextLine,
  RiSurveyLine,
  RiListCheck2,
  RiQuestionnaireLine,
  RiFeedbackLine,
  RiContactsLine,
  RiUserLine,
  RiStoreLine,
  RiShoppingCartLine,
  RiServiceLine,
  RiSettingsLine,
  RiDashboardLine,
} from "@remixicon/react";
import ModernSidebar from "./admin_sidebar";
import ModernHeader from "./modern-header";
import ClipLoader from "react-spinners/ClipLoader";
import { useNavigate } from "react-router-dom";
import RequestModal from "./request-modal";
import CATicketConversation from "../ca-portal/ca-ticket-conversation";
import {
  getCaForms,
  getCaFormById,
  getCaFormResponses,
  submitCaFormResponse,
  getCallToAction,
} from "../utils/api";
import { isGuest } from "../utils/isGuest";
import toast from "react-hot-toast";

// Icon mapper component
const IconMapper = ({ iconName, className }) => {
  const iconMap = {
    // Line icons
    "RiFileTextLine": <RiFileTextLine className={className} />,
    "RiSurveyLine": <RiSurveyLine className={className} />,
    "RiListCheck2": <RiListCheck2 className={className} />,
    "RiQuestionnaireLine": <RiQuestionnaireLine className={className} />,
    "RiFeedbackLine": <RiFeedbackLine className={className} />,
    "RiContactsLine": <RiContactsLine className={className} />,
    "RiUserLine": <RiUserLine className={className} />,
    "RiStoreLine": <RiStoreLine className={className} />,
    "RiShoppingCartLine": <RiShoppingCartLine className={className} />,
    "RiServiceLine": <RiServiceLine className={className} />,
    "RiSettingsLine": <RiSettingsLine className={className} />,
    "RiDashboardLine": <RiDashboardLine className={className} />,
  };

  return iconMap[iconName] || <RiFileTextLine className={className} />;
};

const initialForm = {
  company: "",
  logo: "",
  isNew: false,
  featured: false,
  position: "",
  role: "",
  level: "",
  postedAt: "",
  contract: "",
  district: "",
  salary: "",
  experience: "",
  dateOfJoining: "",
  languages: "",
  tools: "",
  shortDescription: "",
  longDescription: ""
};

const colorSchemes = [
  {
    card: "from-green-100 to-green-50",
    bg: "from-green-500 to-emerald-500",
    text: "text-green-600"
  },
  {
    card: "from-purple-100 to-purple-50",
    bg: "from-purple-500 to-indigo-500",
    text: "text-purple-600"
  },
  {
    card: "from-blue-100 to-blue-50",
    bg: "from-blue-500 to-cyan-500",
    text: "text-blue-600"
  },
  {
    card: "from-yellow-100 to-yellow-50",
    bg: "from-yellow-500 to-amber-500",
    text: "text-yellow-600"
  }
];

const getStatusBadge = (status) => {
  switch (status) {
    case "in_progress":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          In Progress
        </span>
      );
    case "pending":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      );
    case "open":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          {status === "open" ? "Open" : "Pending"}
        </span>
      );
    case "completed":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      );
    case "closed":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Closed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
  }
};

const StatsCardSkeleton = () => (
  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-lg animate-pulse">
    <div className="flex items-center justify-between">
      <div>
        <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
        <div className="h-6 w-12 bg-gray-300 rounded"></div>
      </div>
      <div className="bg-gray-200 p-2 rounded-lg">
        <div className="w-5 h-5"></div>
      </div>
    </div>
  </div>
);

const FormCardSkeleton = () => (
  <div className="group relative bg-white/80 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-lg animate-pulse">
    <div className="relative z-10 flex flex-col items-center text-center">
      <div className="w-12 h-12 bg-gray-200 rounded-xl mb-4"></div>
      <div className="h-5 w-28 bg-gray-300 rounded mb-3"></div>
      <div className="h-3 w-40 bg-gray-200 rounded mb-4"></div>
      <div className="h-10 w-28 bg-gray-300 rounded-lg"></div>
    </div>
  </div>
);

const TableRowSkeleton = () => (
  <tr className="hover:bg-blue-50/50">
    <td className="px-4 py-3 whitespace-nowrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        <div>
          <div className="h-3 w-20 bg-gray-300 rounded"></div>
          <div className="h-2 w-14 bg-gray-200 rounded mt-1"></div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3 whitespace-nowrap">
      <div className="h-3 w-28 bg-gray-200 rounded"></div>
    </td>
    <td className="px-4 py-3 whitespace-nowrap">
      <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
    </td>
    <td className="px-4 py-3 whitespace-nowrap text-right">
      <div className="flex justify-end gap-1">
        <div className="h-7 w-16 bg-gray-300 rounded-md"></div>
        <div className="h-7 w-7 bg-gray-200 rounded-md"></div>
      </div>
    </td>
  </tr>
);

// Expert Modal Component
const ExpertModal = ({ isOpen, onClose, sectorContacts, startupProfile, handleCallExpert }) => {
  if (!isOpen) return null;

  const [caNumber, setCaNumber] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setCaNumber(null); // reset previous number when opening
      getCallToAction()
        .then((res) => {
          if (Array.isArray(res)) {
            const caData = res.find(item => item.role?.toLowerCase().includes("ca"));
            if (caData) setCaNumber(caData.number);
          } else if (res?.role?.toLowerCase().includes("ca")) {
            setCaNumber(res.number);
          }
        })
        .catch((err) => console.error("Error fetching CA number:", err))
        .finally(() => setLoading(false));
    } else {
      // Optional: reset state when modal closes
      setCaNumber(null);
      setLoading(false);
    }
  }, [isOpen]);


  const getSectorPhoneNumber = () => {
    if (!startupProfile || !startupProfile.sector || !sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    const startupSector = startupProfile.sector.toLowerCase().trim();

    // Check if any sector key contains the startup's sector (or vice versa)
    const sectorKey = Object.keys(sectorContacts).find(
      key => key.toLowerCase().includes(startupSector) || startupSector.includes(key.toLowerCase())
    );

    if (sectorKey && sectorContacts[sectorKey] && sectorContacts[sectorKey].length > 0) {
      return sectorContacts[sectorKey][0];
    }

    // If no match found, try to get the "Ca-Portal" contact specifically
    if (sectorContacts["Ca-Portal"] && sectorContacts["Ca-Portal"].length > 0) {
      return sectorContacts["Ca-Portal"][0];
    }

    return null;
  };

  const getAnyContactNumber = () => {
    if (!sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    // First try to get the Ca-Portal contact
    if (sectorContacts["Ca-Portal"] && sectorContacts["Ca-Portal"].length > 0) {
      return sectorContacts["Ca-Portal"][0];
    }

    // If no Ca-Portal contact, get the first available contact
    const firstSector = Object.keys(sectorContacts)[0];
    if (sectorContacts[firstSector] && sectorContacts[firstSector].length > 0) {
      return sectorContacts[firstSector][0];
    }

    return null;
  };

  const hasContacts = () => {
    return sectorContacts && Object.keys(sectorContacts).length > 0 &&
      Object.values(sectorContacts).some(numbers => numbers && numbers.length > 0);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300 expert-modal">
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <PhoneCall className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Our CA Expert</h2>
          <p className="text-gray-600">
            Get personalized assistance with your CA service needs and compliance requirements.
          </p>
        </div>

        {hasContacts() ? (
          <>
            {startupProfile && startupProfile.sector && getSectorPhoneNumber() ? (
              <div className="bg-blue-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sector-Specific Support</h3>
                <p className="text-gray-700 mb-3">
                  Your startup is in the <span className="font-semibold">{startupProfile.sector}</span> sector.
                  Contact our dedicated CA expert for specialized assistance.
                </p>
                <div className="flex items-center justify-center gap-2 text-lg font-semibold text-blue-700">
                  <Phone className="w-5 h-5" />
                  <span>
                    {loading ? "loading..." : caNumber || getSectorPhoneNumber()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-purple-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">General CA Support</h3>
                <p className="text-gray-700 mb-3">
                  Contact our CA experts for assistance with your compliance and financial needs.
                </p>
                <div className="flex items-center justify-center gap-2 text-lg font-semibold text-purple-700">
                  <Phone className="w-5 h-5" />
                  <span>
                    {loading ? "loading..." : caNumber || getAnyContactNumber()}
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Unavailable</h3>
            <p className="text-gray-700 mb-3">
              Our support team contact information is currently being updated.
            </p>
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Please check back later or email us at support@tactos.in
              </p>
            </div>
          </div>
        )}

        {hasContacts() && (
          <button
            onClick={() =>
              handleCallExpert(
                caNumber || getSectorPhoneNumber() || getAnyContactNumber()
              )
            }
            disabled={loading} // prevent clicks while still loading
            className={`w-full bg-gradient-to-r from-blue-600 to-purple-600 
    hover:from-blue-700 hover:to-purple-700 text-white font-semibold 
    py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform 
    hover:scale-105 transition-all duration-200 flex items-center 
    justify-center gap-3 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <PhoneCall className="w-5 h-5" />
            {loading ? "Loading..." : "Call Now"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function AdminCaPortal() {
  const queryClient = useQueryClient();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [activeFormId, setActiveFormId] = useState(null);
  const [selectedFormData, setSelectedFormData] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [conversationFormId, setConversationFormId] = useState(null);
  const [conversationClientName, setConversationClientName] = useState("");
  const [loadingFormId, setLoadingFormId] = useState(null);
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [sectorContacts, setSectorContacts] = useState({});
  const navigate = useNavigate();
  const didFetchRef = useRef(false);
  const isGuestValid=isGuest()

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch sector contacts
  const getSectorContacts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sector-contacts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sector contacts:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchSectorContacts = async () => {
      try {
        const contacts = await getSectorContacts();
        setSectorContacts(contacts);
      } catch (error) {
        console.error('Error fetching sector contacts:', error);
      }
    };

    fetchSectorContacts();
  }, []);

  const { data: forms, isLoading: formsLoading } = useQuery({
    queryKey: ['caForms'],
    queryFn: getCaForms,
    staleTime: 5 * 60 * 1000,
  });

  // Filter forms to only include enabled ones
  const enabledForms = forms?.filter(form => form.enable) || [];

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['startupProfile'],
    queryFn: async () => {
      const startupId = localStorage.getItem("startupId");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`);
      return response.data;
    },
    onSuccess: (data) => {
      setFullName(data.fullName);
      setEmail(data.email);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: tickets = [], isLoading: ticketsLoading } = useQuery({
    queryKey: ['caFormResponses'],
    queryFn: async () => {
      try {
        const responses = await getCaFormResponses();
        return responses.map(response => ({
          id: response.id,
          fullName: response.startup?.startupName ||
            Object.values(response).find(val => typeof val === 'string' && val.match(/[a-zA-Z]/)) ||
            "Unknown Client",
          formName: response.requestName || "Unknown Form",
          status: response.status === "open" ? "pending" : response.status,
          createdAt: response.createdAt,
          rawData: response
        }));
      } catch (error) {
        console.error("Error fetching form responses:", error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
  });

  const { mutate: submitForm } = useMutation({
    mutationFn: (formData) => submitCaFormResponse(formData),
    onSuccess: () => {
      queryClient.refetchQueries(['caFormResponses']);
    }
  });

  const openFormModal = async (form) => {
    setLoadingFormId(form._id);
    try {
      const data = await getCaFormById(form._id);
      setSelectedFormData(data);
      setActiveFormId(form._id);
    } catch (err) {
      console.error("Error fetching form details:", err);
    } finally {
      setLoadingFormId(null);
    }
  };

  const closeFormModal = () => {
    setActiveFormId(null);
    setSelectedFormData(null);
  };

  const handleSubmitForm = async (formValues) => {
    try {
      await submitForm(formValues);
      closeFormModal();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      (ticket.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.formName?.toLowerCase().includes(searchTerm.toLowerCase())) ?? false;
    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewTicket = (ticket) => {
    navigate(`/admin-ca/chat/${ticket.id}`, { state: { ticket } });
  };

  const handleClick = async (form) => {
    setLoadingFormId(form._id);
    await openFormModal(form);
    setLoadingFormId(null);
  };

  const handleCallExpert = (phoneNumber) => {
    const cleanedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
    window.location.href = `tel:${cleanedPhoneNumber}`;
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ModernSidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        fullName={fullName}
        email={email}
      />
      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : sidebarOpen ? "ml-16 md:ml-20 lg:ml-20" : "ml-0"}`}
      >
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-black via-gray-900 to-slate-600 bg-clip-text text-transparent">
                  CA Portal Dashboard
                </h1>
                <p className="text-gray-600 text-sm sm:text-base mt-1 sm:mt-2">Manage CA forms and track client requests</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Add the Call to Expert button here */}
                <button
                  onClick={() => setShowExpertModal(true)}
                  className="group relative overflow-hidden bg-black hover:bg-gray-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  <PhoneCall className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Call to Expert</span>
                </button>

                <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl px-3 py-1.5 sm:px-4 sm:py-2 shadow-sm">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                    <span className="text-xs sm:text-sm text-gray-600">{new Date().toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {formsLoading || ticketsLoading ? (
                <>
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                </>
              ) : (
                <>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Forms</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{enabledForms.length || 0}</p>
                      </div>
                      <div className="bg-blue-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                        <RiFileTextLine className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Requests</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{tickets?.length || 0}</p>
                      </div>
                      <div className="bg-green-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">Pending</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                          {tickets?.filter(t => t.status === "pending").length || 0}
                        </p>
                      </div>
                      <div className="bg-yellow-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">This Month</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                          {tickets?.filter(t => new Date(t.createdAt).getMonth() === new Date().getMonth()).length || 0}
                        </p>
                      </div>
                      <div className="bg-purple-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600" />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* CA Forms Section */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Available CA Forms</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Choose from our comprehensive CA service forms</p>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="text-xs sm:text-sm text-purple-600 font-medium">Professional Services</span>
                </div>
              </div>

              {formsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  <FormCardSkeleton />
                  <FormCardSkeleton />
                  <FormCardSkeleton />
                  <FormCardSkeleton />
                </div>
              ) : enabledForms.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 sm:py-20 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/20 shadow-lg">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 sm:p-6 rounded-full mb-4 sm:mb-6">
                    <RiFileTextLine className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-blue-600" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No Forms Available</h3>
                  <p className="text-gray-600 text-center text-sm sm:text-base max-w-xs sm:max-w-md">
                    There are currently no active forms available. Please check back later.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {enabledForms.map((form, idx) => {
                    const colorScheme = colorSchemes[idx % colorSchemes.length];
                    return (
                      <div
                        key={form._id}
                        className="group relative bg-white/80 backdrop-blur-sm border border-white/20 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-[1.02] flex flex-col h-full"
                      >
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${colorScheme.card} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl sm:rounded-3xl`}
                        />

                        <div className="relative z-10 flex flex-col items-center text-center justify-between flex-1">
                          <div
                            className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gray-600 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                          >
                            <IconMapper iconName={form.icon} className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                          </div>

                          <h3 className="text-md font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-gray-800 transition-colors duration-300">
                            {form.name}
                          </h3>
                          <button
                            onClick={() =>{
                              if(isGuest()){
                                toast.error("Please login to view CA/Forms")
                                navigate("/login")
                              }else{
                                 handleClick(form)
                              }
                            }}
                            disabled={loadingFormId === form._id}
                            className={`
                              group/btn relative overflow-hidden
                              bg-black
                              hover:shadow-xl text-white
                              px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg
                              transform hover:scale-105 transition-all duration-200
                              flex items-center justify-center gap-1 sm:gap-2
                              text-xs sm:text-sm
                              ${loadingFormId ? "cursor-not-allowed opacity-75" : ""}
                            `}
                          >
                            <div
                              className="absolute inset-0 bg-white opacity-0
                              group-hover/btn:opacity-20 transition-opacity duration-200"
                            />
                            {loadingFormId === form._id
                              ? <ClipLoader size={16} color="#ffffff" />
                              : <>
                                <Target className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>Apply Now</span>
                              </>
                            }
                          </button>
                        </div>

                        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className={`w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r ${colorScheme.bg} rounded-full`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tickets Section */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-1 sm:gap-2">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
                    Client Requests
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base">Track and manage all client form submissions</p>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                  <div className="relative w-full sm:w-auto">
                    <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search requests..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-7 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 bg-white/70 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 w-full text-xs sm:text-sm"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/70 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
                {ticketsLoading ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50/50">
                        <tr>
                          <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Client
                          </th>
                          <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Service
                          </th>
                          <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white/50 divide-y divide-gray-200">
                        <TableRowSkeleton />
                        <TableRowSkeleton />
                        <TableRowSkeleton />
                        <TableRowSkeleton />
                      </tbody>
                    </table>
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 sm:py-16">
                    <div className="bg-gray-100 p-4 sm:p-6 rounded-full mb-3 sm:mb-4">
                      <Users className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400" />
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No Requests Found</h3>
                    <p className="text-gray-500 text-center text-xs sm:text-sm">
                      {searchTerm || statusFilter !== "all"
                        ? "No requests match your current filters"
                        : "Client requests will appear here once forms are submitted"}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 sm:px-6 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">Request Management</h3>
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="text-xs sm:text-sm text-gray-600">{filteredTickets.length} requests</span>
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Client
                            </th>
                            <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Service
                            </th>
                            <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white/50 divide-y divide-gray-200">
                          {filteredTickets.map((ticket, idx) => (
                            <tr key={idx} className="hover:bg-blue-50/50 transition-colors duration-200">
                              <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-black via-gray-900 to-slate-600 rounded-full flex items-center justify-center">
                                    <span className="text-white font-semibold text-xs sm:text-sm">
                                      {(ticket.fullName || "Test 1").charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-xs sm:text-sm font-semibold text-gray-900">{ticket.fullName || "Test 1"}</p>
                                    <p className="text-2xs sm:text-xs text-gray-500">Client</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                                  <span className="text-xs sm:text-sm font-medium text-gray-900">{ticket.formName}</span>
                                </div>
                              </td>
                              <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                                {getStatusBadge(ticket.status || "pending")}
                              </td>
                              <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end gap-1 sm:gap-2">
                                  <button
                                    onClick={() => handleViewTicket(ticket)}
                                    className="group relative overflow-hidden bg-black hover:from-blue-700 hover:to-purple-700 text-white px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-md sm:rounded-lg font-medium shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                                  >
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span>View</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {conversationFormId && (
            <div className="bg-white rounded-lg shadow p-4 h-[400px] sm:h-[500px] md:h-[600px] overflow-y-auto">
              <CATicketConversation
                formId={conversationFormId}
                clientName={conversationClientName}
              />
            </div>
          )}

          {activeFormId && selectedFormData && (
            <RequestModal
              formId={selectedFormData.form._id}
              title={selectedFormData.form.name}
              fields={selectedFormData.fields}
              options={selectedFormData.options}
              thankYouMessage={selectedFormData.form.thank_you_message}
              onClose={closeFormModal}
              userProfile={profile} 
              onSubmit={(formData) => {
                handleSubmitForm({
                  ...formData,
                  status: "pending",
                  createdAt: new Date().toISOString(),
                  userEmail: profile?.email,
                  userPhone: profile?.phone
                });
              }}
            />
          )}

          {/* Expert Modal */}
          <ExpertModal
            isOpen={showExpertModal}
            onClose={() => setShowExpertModal(false)}
            sectorContacts={sectorContacts}
            startupProfile={profile}
            handleCallExpert={handleCallExpert}
          />
        </main>
      </div>
    </div>
  );
}